import { useState, useEffect, useCallback, useRef } from 'react';
import { getGuests, createGuest, updateGuest, deleteGuest } from '../lib/api';
import api, { openProtectedFile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { GuestFormDialog } from '../components/guests/GuestFormDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Users, Search, Mail, Phone, Upload, FileCheck, Globe2, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function Guests() {
  const { isAdmin } = useAuth();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    id_type: 'passport',
    id_number: '',
    address: '',
    nationality: ''
  });

  const loadGuests = useCallback(async (search = '') => {
    try {
      const response = await getGuests(search || undefined);
      setGuests(response.data);
    } catch (error) {
      toast.error('Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGuests();
  }, [loadGuests]);

  const handleSearch = (value) => {
    setSearchQuery(value);
    loadGuests(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGuest) {
        await updateGuest(editingGuest.id, formData);
        toast.success('Guest updated successfully');
      } else {
        await createGuest(formData);
        toast.success('Guest created successfully');
      }
      
      setModalOpen(false);
      setEditingGuest(null);
      resetForm();
      loadGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (guest) => {
    setEditingGuest(guest);
    setFormData({
      name: guest.name,
      email: guest.email,
      phone: guest.phone,
      id_type: guest.id_type,
      id_number: guest.id_number,
      address: guest.address || '',
      nationality: guest.nationality || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (guest) => {
    if (!window.confirm(`Are you sure you want to delete ${guest.name}?`)) return;
    
    try {
      await deleteGuest(guest.id);
      toast.success('Guest deleted successfully');
      loadGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete guest');
    }
  };

  const handleUploadId = async (guestId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/guests/${guestId}/upload-id`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('ID document uploaded');
      loadGuests(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFormC = async (guest) => {
    try {
      await api.post(`/guests/${guest.id}/form-c`);
      toast.success(`Form-C marked as submitted for ${guest.name}`);
      loadGuests(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Form-C');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      id_type: 'passport',
      id_number: '',
      address: '',
      nationality: ''
    });
  };

  const openAddModal = () => {
    setEditingGuest(null);
    resetForm();
    setModalOpen(true);
  };

  return (
    <div data-testid="guests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Guests</h1>
          <p className="text-stone-500 mt-1">Manage guest profiles and information</p>
        </div>
        <Button 
          onClick={openAddModal}
          className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm"
          data-testid="add-guest-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Guest
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-guests-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
          </div>
        ) : guests.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No guests found</p>
            <Button 
              onClick={openAddModal}
              variant="outline"
              className="mt-4"
            >
              Add your first guest
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 hover:bg-stone-50">
                <TableHead className="font-semibold text-stone-700">Name</TableHead>
                <TableHead className="font-semibold text-stone-700">Contact</TableHead>
                <TableHead className="font-semibold text-stone-700">ID Type</TableHead>
                <TableHead className="font-semibold text-stone-700">ID Number</TableHead>
                <TableHead className="font-semibold text-stone-700">Nationality</TableHead>
                <TableHead className="font-semibold text-stone-700">Total Stays</TableHead>
                <TableHead className="font-semibold text-stone-700 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guests.map((guest, index) => (
                <TableRow 
                  key={guest.id}
                  className="table-row-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`guest-row-${guest.id}`}
                >
                  <TableCell className="font-medium text-stone-900">{guest.name}</TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 text-stone-600 text-sm">
                        <Mail className="w-3.5 h-3.5" />
                        {guest.email}
                      </div>
                      <div className="flex items-center gap-1.5 text-stone-500 text-sm">
                        <Phone className="w-3.5 h-3.5" />
                        {guest.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize text-stone-600">
                    {!guest.id_number || guest.id_type === 'pending'
                      ? <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded normal-case" data-testid={`id-pending-${guest.id}`}>Collect ID at check-in</span>
                      : (guest.id_type || '').replace('_', ' ')}
                  </TableCell>
                  <TableCell className="text-stone-600 font-mono text-sm">{guest.id_number || '-'}</TableCell>
                  <TableCell className="text-stone-600">{guest.nationality || '-'}</TableCell>
                  <TableCell className="text-stone-600">{guest.total_stays || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Upload ID */}
                      <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf" onChange={(e) => { if (e.target.files[0] && uploadingId) handleUploadId(uploadingId, e.target.files[0]); }} />
                      <Button variant="ghost" size="sm" onClick={() => { setUploadingId(guest.id); fileInputRef.current?.click(); }}
                        className={`h-8 w-8 p-0 ${guest.id_document_url ? 'text-emerald-600' : 'text-stone-400'} hover:text-sage-700 hover:bg-sage-50`}
                        title={guest.id_document_url ? 'ID uploaded' : 'Upload ID'} data-testid={`upload-id-${guest.id}`}>
                        {guest.id_document_url ? <FileCheck className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                      </Button>
                      {guest.id_document_url && (
                        <Button variant="ghost" size="sm" onClick={() => openProtectedFile(guest.id_document_url).catch(() => toast.error('Could not open ID document'))}
                          className="h-8 w-8 p-0 text-stone-500 hover:text-sage-700 hover:bg-sage-50" title="View ID" data-testid={`view-id-${guest.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Form-C for foreign guests */}
                      {guest.is_foreign && (
                        <Button variant="ghost" size="sm" onClick={() => handleFormC(guest)} disabled={guest.form_c_submitted}
                          className={`h-8 px-2 text-xs ${guest.form_c_submitted ? 'text-emerald-600' : 'text-amber-600'}`}
                          title={guest.form_c_submitted ? `Form-C submitted ${guest.form_c_date || ''}` : 'Submit Form-C'}
                          data-testid={`form-c-${guest.id}`}>
                          <Globe2 className="w-3.5 h-3.5 mr-1" />{guest.form_c_submitted ? 'C' : 'C!'}
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(guest)}
                        className="h-8 w-8 p-0 text-stone-500 hover:text-sage-700 hover:bg-sage-50"
                        data-testid={`edit-guest-${guest.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(guest)}
                          className="h-8 w-8 p-0 text-stone-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-guest-${guest.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add/Edit Modal */}
      <GuestFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editingGuest={editingGuest}
      />
    </div>
  );
}
